'use client';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import {
  ACCEPT_VIDEO_MIMETYPE,
  MAX_VIDEO_DURATION_IN_SECONDS,
  MAX_VIDEO_FILE_SIZE,
  MIN_VIDEO_DURATION_IN_SECONDS,
  UPLOAD_SCENE,
  type UploadScene,
} from '@/app/constants';
import { useVideoUpload } from '@/app/hooks';
import type { VideoUploadFile, VideoUploadFileInfo } from '@/app/types';
import { hysdk } from '@/app/utils/client';

export interface HeadlessVideoUploaderProps {
  disabled?: boolean;
  maxDuration?: number;
  minDuration?: number;
  maxSize?: number;
  validateMetadata?: (metadata: {
    duration: number;
    aspectRatio: number;
    width: number;
    height: number;
  }) => string | null;
  scene?: UploadScene;
  /** 支持的视频格式 */
  videoAccept?: string[];
  onChange: (file: VideoUploadFile, fileUploadInfo?: VideoUploadFileInfo) => void;
  /** 是否截取视频首帧作为封面图 */
  captureCoverImage?: boolean;
  /** 是否将截取的封面图上传到服务器 */
  uploadCoverImage?: boolean;
  allowTempMode?: boolean;
  emitUploadingInTempMode?: boolean;
  emitUploadingPreview?: boolean;
}

/**
 * Headless 视频上传组件
 *
 * 只处理隐藏的 input 元素和点击选择视频的逻辑，不包含任何 UI。
 * 适用于需要在更大区域实现拖拽功能的场景。
 *
 * @example
 * ```tsx
 * const handleChange = (file, fileUploadInfo) => {
 *   console.log('视频上传:', file, fileUploadInfo);
 * };
 *
 * <HeadlessVideoUploader
 *   onChange={handleChange}
 *   onTriggerClick={(openFileSelector) => (
 *     <button onClick={openFileSelector}>选择视频</button>
 *   )}
 * />
 * ```
 */
export const HeadlessVideoUploader = forwardRef<
  HTMLInputElement,
  HeadlessVideoUploaderProps & {
    /**
     * 渲染函数，接收 openFileSelector 函数用于触发文件选择和 uploadFile 函数用于处理文件上传
     */
    children?: (props: {
      openFileSelector: (e?: React.MouseEvent) => Promise<void>;
      uploadFile: (file: File) => Promise<void>;
    }) => React.ReactNode;
  }
>(
  (
    {
      disabled,
      maxDuration = MAX_VIDEO_DURATION_IN_SECONDS,
      minDuration = MIN_VIDEO_DURATION_IN_SECONDS,
      maxSize = MAX_VIDEO_FILE_SIZE,
      validateMetadata,
      captureCoverImage = false,
      uploadCoverImage = false,
      scene = UPLOAD_SCENE.VIDU,
      videoAccept = ACCEPT_VIDEO_MIMETYPE,
      allowTempMode = false,
      emitUploadingInTempMode = true,
      emitUploadingPreview = false,
      onChange,
      children,
    },
    ref,
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => fileInputRef.current as HTMLInputElement, []);

    // 使用视频上传 Hook（统一使用 onChange 方式，与图片上传保持一致）
    const { uploadFile, cancelUpload } = useVideoUpload({
      maxDuration,
      minDuration,
      maxSize,
      validateMetadata,
      captureCoverImage,
      uploadCoverImage,
      allowTempMode,
      emitUploadingInTempMode,
      emitUploadingPreview,
      scene,
      videoAccept,
      onChange,
    });

    // 处理文件选择器打开
    const handleOpenFileSelector = async (e?: React.MouseEvent) => {
      if (disabled) return;
      // 检查iOS权限，如果没有权限则不打开文件选择器
      if (hysdk.sniff.isViduApp && hysdk.sniff.os === 'ios') {
        const hasPermission = await hysdk.getAppPermissionInfo(['album', 'camera']);
        if (!hasPermission) {
          e?.stopPropagation();
          return;
        }
      }
      // 手动触发文件选择器
      if (fileInputRef.current) fileInputRef.current.click();
    };

    // 处理文件选择
    const handleFileChange = async (file: File) => {
      await uploadFile(file);
    };

    // 组件卸载时取消上传
    useEffect(() => {
      return () => {
        cancelUpload();
      };
    }, [cancelUpload]);

    return (
      <>
        {children?.({ openFileSelector: handleOpenFileSelector, uploadFile: handleFileChange })}
        <input
          hidden
          accept={videoAccept.join(',')}
          type="file"
          ref={fileInputRef}
          disabled={disabled}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            await handleFileChange(file);
            e.target.value = ''; // 清空文件输入
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </>
    );
  },
);

HeadlessVideoUploader.displayName = 'HeadlessVideoUploader';
